/*
 * Recommended VS Code Extension – "Comment Anchors" exodiusstudios.comment-anchors */
import GlobalFallbackSkeleton from 'packages/lib/common-ui/app-shell/app-layout';
import AnnotationToolSkeleton from 'packages/at-shell/components/app-shell/app-layout';
import { Suspense, lazy } from 'react';
import { Switch, useRouteMatch, Redirect } from 'react-router-dom';
import { ContractorRoute } from 'packages/main/routes/components/private-route';
import AudioTool from 'packages/main/components/audio-tool/audio-tool-landing';
import { ConfigProvider } from 'antd';
import { customDefaultTheme } from 'packages/lib/common-ui/theme';
import { AnnotationToolRoute } from 'packages/main/routes/components/annotation-tool-route';
import AppPageSkeleton from 'packages/lib/common-ui/app-shell/app-layout';
import { LegacyTldRedirect } from 'packages/main/routes/components/legacy-tld-redirect';
// May-20-2026 Used Cursor (Auto / Claude Opus 4.7 / Sonnet 4.6) for Single-Task SE addition

/**
 * ANCHOR - Contractor Portal Pages
 */
const AnnotationTaskPortal = lazy(
  () => import('packages/main/pages/annotation-task-portal/tools')
);
const TaskPipelines = lazy(
  () => import('packages/main/pages/annotation-task-portal/pipelines')
);
const SampleGroupQueues = lazy(
  () => import('packages/main/pages/annotation-task-portal/sample-group-queue')
);
const TldHome = lazy(
  () => import('packages/main/components/tld-home/tld-home-contractor')
);
const TLDTool = lazy(() => import('packages/main/pages/tld-tool'));
/**
 * ANCHOR - Annotation Tool Pages
 */
const SemSegTool = lazy(() => import('packages/main/pages/sem-seg'));
const InstanceSegTool = lazy(
  () => import('packages/main/pages/instance-seg-poc')
);
const ClassificationEnrichmentTool = lazy(
  () => import('packages/main/pages/classification-enrichment')
);
const SceneEnrichmentTool = lazy(
  () => import('packages/main/pages/scene-enrichment')
);
const SceneEnrichmentToolLegacy = lazy(
  () => import('packages/main/pages/scene-enrichment-legacy')
);
const InstanceSegToolLegacy = lazy(
  () => import('packages/main/pages/instance-seg')
);
const EVATool = lazy(() => import('packages/main/pages/eva-tool'));
const LidarTopDown = lazy(() => import('packages/main/pages/lidar-top-down'));
const LidarBox = lazy(() => import('packages/main/pages/lidar-box'));
const LidarSeg = lazy(() => import('packages/main/pages/lidar-seg'));
const LidarBoxEnrichment = lazy(
  () => import('packages/main/pages/lidar-box-enrichment')
);
const LidarBoxAmplification = lazy(
  () => import('packages/main/pages/lidar-box-amplification')
);
const RosterManagementContractor = lazy(
  () => import('packages/main/pages/roster-man')
);
const DataMiningQA = lazy(() => import('packages/main/pages/data-mining-qa'));

/** ANCHOR - Non-annotation tools updated to use at-shell */

const DataDiscovery = lazy(() => import('packages/main/pages/data-discovery'));
const Omnitag = lazy(() => import('packages/main/pages/omnitag'));

/**
 * ANCHOR - Legacy Pages
 * These pages are "Legacy" because they use an outdated Material UI Layout
 */
const Videos = lazy(() => import('packages/main/components/Videos/landing'));

/**
 * ANCHOR - Error Pages
 */
const ErrorPage404 = lazy(
  () => import('packages/lib/common-ui/error-pages/error-page-404')
);

const ErrorPage403 = lazy(
  () => import('packages/lib/common-ui/error-pages/error-page-403')
);

export function Contractor(): JSX.Element {
  const match = useRouteMatch();
  return (
    <ConfigProvider
      theme={{
        ...customDefaultTheme,
        token: {
          borderRadius: 6,
        },
      }}
    >
      <Suspense fallback={<GlobalFallbackSkeleton />}>
        <Switch>
          {/**
           * ANCHOR - Contractor Task Portal Routes
           */}
          <AnnotationToolRoute path={`${match.url}/tools`} exact>
            <Suspense fallback={<AppPageSkeleton />}>
              <AnnotationTaskPortal />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute path={`${match.url}/tasks`} exact>
            <Suspense fallback={<AppPageSkeleton />}>
              <AnnotationTaskPortal />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute exact path={`${match.url}`}>
            <Redirect to="/contractor/tools" />
          </AnnotationToolRoute>
          <AnnotationToolRoute path={`${match.url}/tasks/:workflow`} exact>
            <Suspense fallback={<AppPageSkeleton />}>
              <TaskPipelines />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path={`${match.url}/tasks/:workflow/:pipeline/:task_state`}
            exact
          >
            <Suspense fallback={<AppPageSkeleton />}>
              <SampleGroupQueues />
            </Suspense>
          </AnnotationToolRoute>
          {/**
           * ANCHOR - Contractor Tool Routes
           */}

          <AnnotationToolRoute path={`${match.url}/tld-home`} exact>
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <TldHome />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path={`${match.url}/traffic-light-detection/:extraction_token/:task_token?`}
            exact
          >
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <TLDTool />
            </Suspense>
          </AnnotationToolRoute>
          {/* Support legacy links created before TLD tool was integrated with Task Management */}
          <AnnotationToolRoute
            path={`${match.url}/tld-video/:extraction_token/:readonly?`}
            exact
          >
            <LegacyTldRedirect baseUrl={match.url} />
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path={`${match.url}/sem-seg/:extraction_token/:task_token?`}
            exact
          >
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <SemSegTool />
            </Suspense>
          </AnnotationToolRoute>

          <AnnotationToolRoute
            path={`${match.url}/eva/:extraction_token/:task_token?`}
            exact
          >
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <EVATool />
            </Suspense>
          </AnnotationToolRoute>

          <AnnotationToolRoute
            path={`${match.url}/instance-seg/:extraction_token/:task_token?`}
            exact
          >
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <InstanceSegTool />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path={`${match.url}/instance-seg-legacy/:extraction_token/:task_token?`}
            exact
          >
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <InstanceSegToolLegacy />
            </Suspense>
          </AnnotationToolRoute>

          <AnnotationToolRoute
            path={`${match.url}/classification-enrichment`}
            exact
          >
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <ClassificationEnrichmentTool />
            </Suspense>
          </AnnotationToolRoute>

          <AnnotationToolRoute
            path={`${match.url}/scene-enrichment/:extraction_token/:task_token?`}
            exact
          >
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <SceneEnrichmentTool />
            </Suspense>
          </AnnotationToolRoute>

          <AnnotationToolRoute
            path={`${match.url}/scene-enrichment-legacy`}
            exact
          >
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <SceneEnrichmentToolLegacy />
            </Suspense>
          </AnnotationToolRoute>

          <AnnotationToolRoute
            path={`${match.url}/lidar-box/:extraction_token/:task_token?`}
            exact
          >
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme="dark" />}
            >
              <LidarBox />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path={`${match.url}/lidar-top-down/:extraction_token/:task_token?`}
            exact
          >
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme="dark" />}
            >
              <LidarTopDown />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path={`${match.url}/lidar-seg/:extraction_token/:task_token?`}
            exact
          >
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme="dark" />}
            >
              <LidarSeg />
            </Suspense>
          </AnnotationToolRoute>

          <AnnotationToolRoute
            path={`${match.url}/lidar-box-enrichment/:extraction_token/:task_token?`}
            exact
          >
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme="dark" />}
            >
              <LidarBoxEnrichment />
            </Suspense>
          </AnnotationToolRoute>

          <AnnotationToolRoute
            path={`${match.url}/lidar-box-amplification/:extraction_token/:task_token?`}
            exact
          >
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme="dark" />}
            >
              <LidarBoxAmplification />
            </Suspense>
          </AnnotationToolRoute>

          <AnnotationToolRoute path={`${match.url}/data-mining-qa`} exact>
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme="light" />}
            >
              <DataMiningQA />
            </Suspense>
          </AnnotationToolRoute>

          <AnnotationToolRoute
            path={`${match.url}/data-discovery/:query?`}
            exact
          >
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme="dark" />}
            >
              <DataDiscovery />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute path={`${match.url}/omnitag/:query?`} exact>
            <Suspense
              fallback={<AnnotationToolSkeleton skeletonMode theme="dark" />}
            >
              <Omnitag />
            </Suspense>
          </AnnotationToolRoute>
          <AnnotationToolRoute path={`${match.url}/roster-management`}>
            <Suspense fallback={<AnnotationToolSkeleton skeletonMode />}>
              <RosterManagementContractor />
            </Suspense>
          </AnnotationToolRoute>

          {/**
           * ANCHOR - Legacy Routes
           * "Legacy" because they depend on outdated Material UI layouts & components
           */}
          <ContractorRoute
            path={`${match.url}/scene-hunting`}
            component={Videos}
          />
          <ContractorRoute
            path={`${match.url}/audio-tool/:mode?`}
            component={AudioTool}
          />
          {/**
           * ANCHOR - Handle awkwardly non-existent paths
           */}
          <AnnotationToolRoute path={`${match.url}/tasks`} exact>
            <Redirect to={`${match.url}/tools`} />
          </AnnotationToolRoute>
          <AnnotationToolRoute
            path={`${match.url}/tasks/:workflow/:no_path`}
            exact
          >
            <Redirect to={`${match.url}/tools`} />
          </AnnotationToolRoute>
          <AnnotationToolRoute
            exact
            path={`${match.url}/tasks/sem-seg/:no_path`}
          >
            <Redirect to={`${match.url}/tasks/sem-seg`} />
          </AnnotationToolRoute>
          <AnnotationToolRoute
            exact
            path={`${match.url}/tasks/lidar-box/:no_path`}
          >
            <Redirect to={`${match.url}/tasks/lidar-box`} />
          </AnnotationToolRoute>
          <AnnotationToolRoute
            exact
            path={`${match.url}/tasks/lidar-seg/:no_path`}
          >
            <Redirect to={`${match.url}/tasks/lidar-seg`} />
          </AnnotationToolRoute>
          <AnnotationToolRoute exact path={`${match.url}/tasks/eva/:no_path`}>
            <Redirect to={`${match.url}/tasks/eva`} />
          </AnnotationToolRoute>
          <AnnotationToolRoute
            exact
            path={`${match.url}/tasks/audio-labeling/:no_path`}
          >
            <Redirect to={`${match.url}/tasks/audio-labeling`} />
          </AnnotationToolRoute>
          <AnnotationToolRoute path={`${match.url}/403`} exact>
            <ErrorPage403 />
          </AnnotationToolRoute>
          <AnnotationToolRoute path={`${match.url}/:no_path`} exact>
            <ErrorPage404 />
          </AnnotationToolRoute>
          <AnnotationToolRoute path={`${match.url}/:no_path/*`} exact>
            <ErrorPage404 />
          </AnnotationToolRoute>
        </Switch>
      </Suspense>
    </ConfigProvider>
  );
}

export default Contractor;
